import { usePreferences } from '../../hooks/usePreferences'
import { useToast } from '../../hooks/useToast'

export default function PreferencesSettings() {
  const { preferences, set, loading } = usePreferences()

  const { toast, show: showToast } = useToast({ durationMs: 2500 })

  const handleChange = async <K extends keyof typeof preferences>(
    key: K,
    value: typeof preferences[K],
    successMsg: string,
  ) => {
    const { error } = await set(key, value)
    if (error) showToast(`Failed to save: ${error.message}`)
    else showToast(successMsg)
  }

  if (loading) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white p-6 text-sm text-zinc-400">
        Loading preferences...
      </div>
    )
  }

  return (
    <div className="relative flex flex-col gap-6">
      <header>
        <h1 className="text-lg font-semibold text-zinc-900">Preferences</h1>
        <p className="text-sm text-zinc-500">These settings sync across all your devices.</p>
      </header>

      {/* Default view */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-zinc-900">Default view</h2>
        <p className="mt-0.5 text-xs text-zinc-500">Which list to open when you launch Slash.</p>
        <select
          value={preferences.defaultView}
          onChange={(e) => handleChange('defaultView', e.target.value, 'Default view updated')}
          className="mt-3 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none"
        >
          <option value="INBOX">Inbox</option>
          <option value="UNREAD">Inbox (unread only)</option>
          <option value="STARRED">Starred</option>
          <option value="DRAFTS">Drafts</option>
          <option value="REMINDERS">Reminders</option>
          <option value="ARCHIVE">Archive</option>
          <option value="INMAIL">InMail</option>
          <option value="SPONSORED">Sponsored</option>
          <option value="SPAM">Spam</option>
          <option value="SALESNAV">Sales Nav</option>
        </select>
      </section>

      {/* Stale invitation threshold */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-zinc-900">Stale invitation threshold</h2>
        <p className="mt-0.5 text-xs text-zinc-500">
          How many days before a pending invitation is flagged as stale and eligible for bulk-withdraw.
        </p>
        <select
          value={preferences.withdrawThreshold}
          onChange={(e) => handleChange('withdrawThreshold', Number(e.target.value), 'Threshold updated')}
          className="mt-3 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none"
        >
          <option value={7}>1 week</option>
          <option value={14}>2 weeks</option>
          <option value={21}>3 weeks</option>
          <option value={30}>1 month</option>
        </select>
      </section>

      {/* Theme */}
      <section className="rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-zinc-900">Theme</h2>
            <p className="mt-0.5 text-xs text-zinc-500">
              Your preference syncs across devices. Full dark mode ships in a later update.
            </p>
          </div>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-medium text-zinc-500">
            Preview
          </span>
        </div>
        <select
          value={preferences.theme}
          onChange={(e) => handleChange('theme', e.target.value as 'system' | 'light' | 'dark', 'Theme preference saved (applies in a future update)')}
          className="mt-3 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-sm focus:border-zinc-400 focus:outline-none"
        >
          <option value="system">Match system</option>
          <option value="light">Light</option>
          <option value="dark">Dark</option>
        </select>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2.5 shadow-lg">
          <span className="text-xs text-white">{toast}</span>
        </div>
      )}
    </div>
  )
}
